import { useState } from "react";

import { useTheme } from "@mui/material/styles";
import CircularProgress from "@mui/material/CircularProgress";
import RadioGroup from "@mui/material/RadioGroup";
import Radio from "@mui/material/Radio";
import Typography from "@mui/material/Typography";

import { useElements } from "@stripe/react-stripe-js";

import strings from "core/strings";
import useToggle from "core/hooks/useToggle";
import Box from "ui/atom/Box";
import Icon from "ui/atom/Icon";

import Accordion from "ui/base/Accordion";
import Alert from "ui/base/Alert";
import Button from "ui/base/Button";
import Card from "ui/base/Card";
import More from "ui/base/More";
import Modal from "ui/Modal";
import Stripe from "ui/Stripe";

import { useCardRemove, useCards, useCardSetup, usePay } from "features/pay/fabric";
import CheckboxTile from "features/pay/ui/CheckboxTile";


const Add = () => {
	const pay = usePay();
	const cards = useCards();
	const [loading, loadingToggle] = useToggle();
	const elements = useElements();

	const click = async () => {
		loadingToggle();
		await cards.confirm(elements);
		loadingToggle();
	};

	return (
		<Box.Column gap={1}>
			<Button fullWidth variant="contained" color="tertiary" onClick={click} loading={loading || pay.mutation.isPending || cards.add.isPending}>
				Add Card
			</Button>
			<Button fullWidth onClick={cards.store.showCards} disabled={loading || pay.mutation.isPending || cards.isLoading}>
				Back
			</Button>
		</Box.Column>
	);
};

const Item = ({ title, card, icon, radio, setMethod, deleteMethod, action = false }) => {
	const [open, setOpen] = useState(false);
	const cards = useCards();
	const theme = useTheme();

	if (!card) return null;
	const copy = cards.copy(card.brand, card.last4);
	const act = cards.store.confirmation.action;

	const style = {
		root: {
			alignItems: "center",
			justifyContent: "space-between",
			p: theme.spacing(1, 0),
			cursor: !action ? "pointer" : undefined,
			width: "100%",
			":hover": !action ? { backgroundColor: "rgba(0, 0, 0, 0.05)" } : undefined
		},
		content: { alignItems: "center" },
		text: { textAlign: "left" },
		radio: { p: 0 },
		button: {
			p: theme.spacing(0.375, 1.25),
			mr: theme.spacing(0.5)
		},
		action: { alignItems: "center" }
	};

	const setDefault = () => {
		setOpen(true);
		cards.store.setConfirmation(copy.default);
	};
	const setDelete = () => {
		setOpen(true);
		cards.store.setConfirmation(copy.delete);
	};

	const cancel = () => {
		setOpen(false);
		cards.store.resetConfirmation();
	};

	const save = () => {
		cards.store.confirmation.type === "delete" ? deleteMethod(card) : setMethod(card);
		setOpen(false);
	};

	const click = () => !action && setMethod && setMethod(card);

	const actions = [
		{ label: act.cancel.text, color: act.cancel.color, onClick: cancel },
		{ label: act.save.text, color: act.save.color, onClick: save, loading: cards.isLoading }
	];

	return card && (
		<Box gap={2} onClick={click} sx={style.root}>
			<Box gap={2} sx={style.content}>
				{icon && <Icon name="credit-card" size={22} />}
				{radio && <Radio color="tertiary" size="small" value={card.id} sx={style.radio} onChange={() => !action ? setMethod(card) : setDefault()} />}
				<Box.Column sx={style.text}>
					{title && <Typography variant="caption">Payment method</Typography>}
					<Typography variant="body3">{card.brand} ending in {card.last4}</Typography>
				</Box.Column>
			</Box>
			<Box gap={1} sx={style.action}>
				{card.wallet && <Icon name={strings.toKebabCase(card.wallet)} size={24} />}
				{!action ? null : card.default
					? (
						<Box sx={{ p: 1 }} title="Default card">
							<Icon name="credit-card" color="white" fill={theme.palette.text.primary} />
						</Box>
					)
					: (
						<More>
							<More.Item icon="credit-card" onClick={setDefault}>Set default</More.Item>
							<More.Item icon="trash-2" onClick={setDelete}>Delete</More.Item>
						</More>
					)}
			</Box>
			<Modal.Confirmation open={open} title={cards.store.confirmation.title} text={cards.store.confirmation.text} actions={actions} onClose={cancel} />
		</Box>
	);
};

PaymentMethods.List = ({ list, selected, setMethod, deleteMethod, setAdding, action = false, loading, ...props }) => {
	const theme = useTheme();

	const style = {
		root: {
			borderRadius: "12px !important",
			gap: 0
		},
		details: {
			display: "flex",
			flexDirection: "column"
		},
		group: {
			gap: theme.spacing(1.25)
		},
		button: {
			mt: theme.spacing(1.5),
			fontSize: "14px"
		}
	};

	return list.length > 0 && (
		<Accordion disableGutters {...props} sx={{ ...style.root, ...props.sx }}>
			<Accordion.Summary>
				<Item title icon card={selected} />
			</Accordion.Summary>
			<Accordion.Details sx={style.details}>
				<RadioGroup value={selected?.id} sx={style.group}>
					{list?.map((c, i) => <Item card={c} radio action={action} setMethod={setMethod} deleteMethod={deleteMethod} key={i} />)}
				</RadioGroup>
				<Button color="tertiary" fullWidth onClick={setAdding} loading={loading} sx={style.button}>+ Add new payment method</Button>
			</Accordion.Details>
		</Accordion>
	);
};

PaymentMethods.Skeleton = ({ show }) => {
	const style = {
		width: "100%", height: "300px",
		display: show ? "flex" : "none",
		flexDirection: "column",
		justifyContent: "center", alignItems: "center",
		gap: 2
	};

	return (
		<Card sx={style}>
			<Typography>Loading payment methods...</Typography>
			<CircularProgress />
		</Card>
	);
};

export default function PaymentMethods({ click, ...props }) {
	const cards = useCards();
	const remove = useCardRemove();
	const setup = useCardSetup();
	const theme = useTheme();
	const [ready, setReady] = useState(false);
	const isDark = theme.palette.mode === "dark";

	const appearance = {
		theme: isDark ? "night" : "stripe",
		rules: {
			".Label": {
				fontFamily: "Franklin Gothic Demi, sans-serif",
				fontSize: "12px", fontWeight: 700, lineHeight: "140%"
			},
			".Input": {
				fontSize: "16px",
				boxShadow: "none",
				padding: "16px 12px",
				borderRadius: "12px",
				border: isDark ? "1px solid #fff" : undefined,
				backgroundColor: isDark ? "#00000000" : undefined
			},
			".Input::placeholder": isDark ? { color: "white" } : undefined
		},
		variables: {
			fontFamily: "Franklin Gothic Book, sans-serif",
			colorBackground: isDark ? theme.palette.secondary.dark : undefined
		}
	};

	const options = { appearance, loader: "always", clientSecret: cards.store.intent };

	if (cards.isLoading) return <PaymentMethods.Skeleton show />;
	if (cards.data?.length && !cards.store.adding) return (
		<PaymentMethods.List
			list={cards.data}
			selected={cards.store.method}
			setMethod={click || cards.store.setMethod}
			setAdding={setup.mutate}
			loading={cards.isLoading}
			deleteMethod={remove.mutate}
			{...props}
		/>
	);

	return cards.stripe && cards.store.intent && (
		<>
			<Alert.Error errors={cards.store.error?.errors?.[""]} />
			<PaymentMethods.Skeleton show={!ready} />
			<Box.Column gap={2} sx={{ display: ready ? "flex" : "none" }}>
				<Stripe stripe={cards.stripe} options={options} key={cards.store.intent}>
					<Stripe.Element onReady={() => setReady(true)} />
					{ready && (
						<>
							<CheckboxTile checked={cards.store.default} onChange={cards.store.toggleDefault}>
								<Typography variant="body3">Make this my default card</Typography>
							</CheckboxTile>
							<Add />
						</>
					)}
				</Stripe>
			</Box.Column>
		</>
	);
}